<template>
  <div class="container">
    <bonus-title initial-tab-value="milestones" />
    <donut-page-sections-one-column title="Annual Events: Anniversaries and Birthdays" class="m-tx4">
      <div class="t dark-gray">
        <span>
          Never forget a special occasion. Automatically send {{ pointsNoun }} on work anniversaries and team birthdays.&#32;
          Donut will send a Slack DM to mark the occasion with your chosen message and milestone bonus.
        </span>
        <a href="/rewards/settings" class="link ul">{{ rewardsStoreNoun }} settings</a>
        <span>.</span>
      </div>
    </donut-page-sections-one-column>
    <!-- Anniversary -->
    <donut-page-sections-two-column class="m-tx3">
      <template #left-side>
        <div class="f">
          <div class="t">Anniversary {{ pointsNoun }}</div>
          <donut-input-toggle v-model="anniversaryToggleSelection" :paywall="paywall('anniversary')" size="small" class="m-lx2" />
        </div>
        <bonus-milestone-configs-manager-reminders v-if="!paywalled" milestone-type="anniversary" />
        <bonus-milestone-configs-next-event-copy v-if="!paywalled" milestone-type="anniversary" class="m-tx1" />
        <bonus-milestone-configs-anniversary-accordion :disabled="paywalled" />
        <donut-button class="inv m-tx2" :disabled="paywalled" @click="openCustomizeMessageModal('anniversary')">Customize anniversary message</donut-button>
      </template>
      <template #right-side>
        <div class="t">Anniversary direct message</div>
        <bonus-milestone-configs-slack-preview milestone-type="anniversary" class="m-tx1" />
      </template>
    </donut-page-sections-two-column>
    <div class="separator m-vx3" />
    <!-- Birthday -->
    <donut-page-sections-two-column>
      <template #left-side>
        <div class="f">
          <div class="t">Birthday {{ pointsNoun }}</div>
          <donut-input-toggle v-model="birthdayToggleSelection" :paywall="paywall('birthday')" size="small" class="m-lx2" />
        </div>
        <bonus-milestone-configs-manager-reminders v-if="!paywalled" milestone-type="birthday" />
        <bonus-milestone-configs-next-event-copy v-if="!paywalled" milestone-type="birthday" class="m-tx1" />
        <div class="f m-t">
          <div class="w-x10">
            <donut-input-async-number :model-value="birthdayPointsValue" class="full-width" :disabled="paywalled" @submit="onBirthdayPointsValueSubmit" />
          </div>
          <div class="t m-lx1">
            <span v-emoji="`:${state.pointsConfig.config.emoji}:`" />
            <span> = ${{ (birthdayPointsValue * state.pointsConfig.dollarsPerPoint).toFixed(2) }} per recipient</span>
          </div>
        </div>
        <div v-emoji="getters['pointsConfig/exchangeRateText']" class="t-sm gray m-t" />
        <donut-button class="inv m-tx2" :disabled="paywalled" @click="openCustomizeMessageModal('birthday')">Customize birthday message</donut-button>
      </template>
      <template #right-side>
        <div class="t">Birthday direct message</div>
        <bonus-milestone-configs-slack-preview milestone-type="birthday" class="m-tx1" />
      </template>
    </donut-page-sections-two-column>
    <div class="separator m-vx3" />
    <!-- Recipients -->
    <bonus-milestone-configs-recipients />
  </div>
</template>

<script setup>
  import { computed, inject } from 'vue';
  import { useStore } from 'vuex';

  const $i18n = inject('$i18n');
  const { state, getters, commit, dispatch } = useStore();

  const pointsNoun = $i18n.t('shoutouts.point', { count: 2 });
  const rewardsStoreNoun = $i18n.t('shoutouts.rewards_store');

  // Anniversary
  const anniversaryToggleSelection = computed({
    get() { return state.bonusMilestoneConfigs.anniversary.active; },
    set(value) {
      dispatch('bonusMilestoneConfigs/updateMilestoneConfig', { type: 'anniversary', params: { active: value } })
        .then(() => { dispatch('setSuccessToast', `Successfully turned ${value ? 'on Anniversary points!' : 'off Anniversary points.'}`); })
        .catch(() => { dispatch('setErrorToast', 'Failed to update Anniversary points.'); });
    },
  });

  // Birthday
  const birthday = computed(() => state.bonusMilestoneConfigs.birthday);
  const birthdayToggleSelection = computed({
    get() { return birthday.value.active; },
    set(value) {
      dispatch('bonusMilestoneConfigs/updateMilestoneConfig', { type: 'birthday', params: { active: value } })
        .then(() => { dispatch('setSuccessToast', `Successfully turned ${value ? 'on Birthday points!' : 'off Birthday points.'}`); })
        .catch(() => { dispatch('setErrorToast', 'Failed to update Birthday points.'); });
    },
  });
  const birthdayPointsValue = computed(() => birthday.value.default_points);
  const onBirthdayPointsValueSubmit = (value, onComplete) => {
    const oldValue = birthdayPointsValue.value;
    if (value < 1 || parseInt(value) !== value) {
      onComplete({ value: oldValue, icon: 'error' });
      dispatch('setErrorToast', 'Value must be a whole number greater than 0.');
    } else {
      dispatch('bonusMilestoneConfigs/updateMilestoneConfig', { type: 'birthday', params: { default_points: value } })
        .then(() => onComplete({ icon: 'success' }))
        .catch(() => {
          onComplete({ value: oldValue, icon: 'error' });
          dispatch('setErrorToast', 'Error updating the points for Birthdays.');
        });
    }
  };

  // Modal
  const openCustomizeMessageModal = (milestoneType) => {
    commit('updateModal', {
      visible: true,
      size: 'lg',
      subcomponent: 'bonus-milestone-configs-modals-message',
      data: {
        milestoneType,
        message: state.bonusMilestoneConfigs[milestoneType].message,
        gifUrl: state.bonusMilestoneConfigs[milestoneType].image_url,
      },
    });
  };

  const paywall = (milestoneType) => ({
      data: state.pointsConfig.config.upgrade_data,
      feature: 'bonus_events_available?',
      featureTitleText: 'enable Annual Events',
      source: `annual-events-${milestoneType}-toggle`,
      programKey: 'shoutouts',
    });
  const paywalled = computed(() => {
    const milestoneType = 'anniversary'; // Arbitrary, same paywall applies to both
    return !!paywall(milestoneType).data['bonus_events_available?'].paywall;
  });
</script>
